/* eslint-disable import/no-unused-modules */ // This is the application entry point
import React, { useEffect, useState } from "react";
import "./index.scss";
import App from "./App";
// Import build tool generated manifest file
import arbManifest from "../public/translations/arbManifest";
import {
  AsyncMessageBundleManager,
  createAsyncMessageBundleManager,
  LocalizationContextBuilder,
} from "@amzn/arb-tools";
import { MbmProvider } from "@amzn/react-arb-tools";
import { Provider } from "react-redux";
import { setupStore } from "./reduxStore/store";
import { getLocale } from "./utility/locale-helper";
import { formatMessage } from "./utility/message-format";
import { RUMContext } from "./rum/rum-context";
import { initAwsRumClient } from "./rum";
import { AwsRum } from "aws-rum-web";
import { AuthOrRedirect } from "./components/auth/redirect";
import { assetsEndpoint } from "./helpers/get-settings";

class ResourceFetchError extends Error {
  constructor(message: string, public retry: boolean) {
    super(message);
  }
}

const RootChecklistUI = () => {
  const [rumClient, setRumClient] = useState<AwsRum | undefined>(undefined);
  const [store, setStore] = useState<any>(null);
  const [mbm, setMbm] = useState<AsyncMessageBundleManager | null>(null);

  useEffect(() => {
    const initialize = async () => {
      const rum_client = await initAwsRumClient();

      // Intercept console error messages and report them. Uses custom property to avoid infinite wrapping
      if (rumClient && !(console.error as { ash_wrapped?: boolean }).ash_wrapped) {
        const err = console.error;

        console.error = function (...args: unknown[]) {
          // Still print to console to allow local debugging
          err.apply(console, args);
          // Submit an error event to RUM with the message for analysis
          rumClient!.recordError(
            new ErrorEvent("console_error", {
              message: formatMessage(args),
            })
          );
        };

        (console.error as { ash_wrapped?: boolean }).ash_wrapped = true;
      }

      setRumClient(rum_client);

      // Setup the store
      const storeInstance = setupStore({});
      setStore(storeInstance);

      const localizationContextProvider = () =>
        new LocalizationContextBuilder().withLocale(getLocale()).withDefaultLocale("en-US").build();

      const messageBundleManager = createAsyncMessageBundleManager({
        arbManifest,
        localizationContextProvider,
        resolveBundleUrl: (url) =>
          url ? `${assetsEndpoint}/translations/${url}` : `${assetsEndpoint}/translations/translation.json`,
        resolveResourcePack: async (url) => {
          const response = await fetch(url, { headers: { "Cache-Control": "no-store" } });
          if (!response.ok) {
            const retry = response.status >= 500 && response.status < 600;
            console.error(`Failed to fetch ${url}: ${response.statusText}`);
            throw new ResourceFetchError(`Failed to fetch ${url}`, retry);
          }
          return response.json();
        },
      });

      setMbm(messageBundleManager);
    };

    initialize();
  }, []);

  if (!rumClient || !store || !mbm) {
    return <div />;
  }

  return (
    <RUMContext.Provider value={rumClient}>
      <Provider store={store}>
        <MbmProvider mbm={mbm}>
          <React.StrictMode>
            <AuthOrRedirect>
              <App />
            </AuthOrRedirect>
          </React.StrictMode>
        </MbmProvider>
      </Provider>
    </RUMContext.Provider>
  );
};

export default RootChecklistUI;
